// src/tabs/logements/WifiNetworksEditor.jsx — FIX WiFi source unique (Front 2)
//
// DÉCISION Dimitri : la SOURCE UNIQUE Wi-Fi est `livret_config.wifi_networks`
// (table V2, array [{ssid, password}]). C'est déjà la source du livret voyageur
// (LivretWifiCard) ET de {wifi_instructions} (dérivé, BUG39). Ce composant rend
// les 2 champs SSID + mot de passe ÉDITABLES depuis la carte 📶 WiFi de l'onglet
// 📋 Instructions (LivretTab) — écrivant cette MÊME source, plus de doublon
// texte libre.
//
// Self-contained : fetch `livret_admin_data` au mount pour le wifi_networks
// courant si non fourni en prop. Sauvegarde via l'action serveur EXISTANTE
// `livret_config_upsert` (PATCH partiel — n'écrase que wifi_networks). Pas de
// fire-and-forget : { ok, json } géré + état error/saving/saved.
//
// Réutilisé DANS la carte WiFi d'Instructions (LivretTab). Édite 1 réseau
// principal (ssid + password) — préserve les éventuels réseaux additionnels
// existants (index 1+) pour ne pas casser le multi-réseaux de LivretInfosEditor.

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Premier réseau d'un tableau wifi_networks (ou {} si vide/invalide).
function firstNetwork(arr) {
  if (Array.isArray(arr) && arr[0] && typeof arr[0] === "object") return arr[0];
  return {};
}

/**
 * @param {object} props
 * @param {string} props.logement_id - id du logement (filter livret_config)
 * @param {Array|undefined} props.wifiNetworks - valeur déjà chargée par le parent
 *   (mutualise le fetch LivretTab). Si undefined → fetch interne au mount.
 * @param {(networks: Array) => void} [props.onSaved] - notifié après save OK avec
 *   le nouveau tableau wifi_networks (permet au parent de re-sync son ctx aperçu).
 * @param {boolean} [props.parentManaged] - true si le PARENT possède le fetch de
 *   wifi_networks (LivretTab le mutualise avec l'aperçu) : l'éditeur ne fetch
 *   alors PAS lui-même (évite le double fetch / le flip non-contrôlé→contrôlé)
 *   et se contente d'attendre la prop (undefined = chargement parent en cours).
 */
export default function WifiNetworksEditor({
  logement_id,
  wifiNetworks,
  onSaved,
  parentManaged = false,
}) {
  // Source : prop si fournie (parent a déjà chargé), sinon fetch interne.
  // Si parentManaged, on ne fetch jamais soi-même même quand la prop est encore
  // undefined (le parent fournira la valeur après son propre chargement).
  const controlled = parentManaged || wifiNetworks !== undefined;

  const [networks, setNetworks] = useState(
    Array.isArray(wifiNetworks) ? wifiNetworks : [],
  );
  const [ssid, setSsid] = useState(() => firstNetwork(wifiNetworks).ssid || "");
  const [password, setPassword] = useState(
    () => firstNetwork(wifiNetworks).password || "",
  );

  // Loading : en mode parentManaged, on attend que la prop devienne définie ;
  // en mode self-fetch, on attend le fetch interne ; en mode contrôlé pur
  // (prop déjà définie), pas de loading.
  const [loading, setLoading] = useState(
    parentManaged ? wifiNetworks === undefined : !controlled,
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync depuis la prop quand le parent la fournit/rafraîchit (anti-pattern
  // F2 : useState(prop) sans useEffect re-sync).
  useEffect(() => {
    if (!controlled) return;
    // parentManaged + prop encore undefined = chargement parent en cours :
    // on attend sans écraser l'état local (garde l'indicateur de chargement).
    if (parentManaged && wifiNetworks === undefined) {
      setLoading(true);
      return;
    }
    const arr = Array.isArray(wifiNetworks) ? wifiNetworks : [];
    setNetworks(arr);
    setSsid(firstNetwork(arr).ssid || "");
    setPassword(firstNetwork(arr).password || "");
    setLoading(false);
  }, [controlled, parentManaged, wifiNetworks]);

  // Fetch interne (mode non contrôlé) — livret_admin_data renvoie config.wifi_networks.
  const loadOwn = useCallback(async () => {
    if (controlled || !logement_id) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(logement_id)}`,
        { method: "GET", headers },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      const arr = Array.isArray(json?.config?.wifi_networks)
        ? json.config.wifi_networks
        : [];
      setNetworks(arr);
      setSsid(firstNetwork(arr).ssid || "");
      setPassword(firstNetwork(arr).password || "");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [controlled, logement_id]);

  useEffect(() => {
    loadOwn();
  }, [loadOwn]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const trimmedSsid = ssid.trim();
      const trimmedPassword = password.trim();
      // Recompose le tableau : remplace le réseau principal (index 0) et
      // préserve les réseaux additionnels existants (index 1+). Si SSID vide →
      // pas de réseau principal (on garde uniquement les additionnels).
      const rest = Array.isArray(networks) ? networks.slice(1) : [];
      const head = trimmedSsid
        ? [{ ssid: trimmedSsid, password: trimmedPassword }]
        : [];
      const nextNetworks = [...head, ...rest];

      const headers = await getAuthHeaders();
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_config_upsert",
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            action: "livret_config_upsert",
            logement_id,
            wifi_networks: nextNetworks,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || "patch_failed");
      setNetworks(nextNetworks);
      setSaved(true);
      onSaved?.(nextNetworks);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ fontSize: 12, color: "#888", padding: "6px 0" }}>
        Chargement du Wi-Fi…
      </div>
    );
  }

  return (
    <div>
      <div style={lbl}>📶 Wi-Fi du logement (source unique)</div>
      {error && (
        <div
          style={{
            padding: 10,
            background: "#fce8e8",
            border: `1px solid ${DS.red}`,
            borderRadius: 7,
            color: DS.red,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          ❌ {error}
        </div>
      )}
      <div
        className="fbz-grid-collapse"
        style={{
          display: "grid",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div>
          <div style={subLbl}>Identifiant (SSID)</div>
          <input
            type="text"
            value={ssid}
            onChange={(e) => {
              setSsid(e.target.value);
              setSaved(false);
            }}
            placeholder="ex: FullBnB-Cocon"
            style={inpStyle}
          />
        </div>
        <div>
          <div style={subLbl}>Mot de passe</div>
          <input
            type="text"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setSaved(false);
            }}
            placeholder="ex: zen2026"
            style={inpStyle}
          />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "8px 16px",
            background: saving ? "#cbd5e1" : DS.skyDeep,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: saving ? "not-allowed" : "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          {saving ? "Enregistrement…" : "💾 Enregistrer le Wi-Fi"}
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
            ✓ Enregistré
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, color: "#888", marginTop: 6 }}>
        Affiché tel quel dans le livret voyageur et injecté dans la variable{" "}
        <code>{"{wifi_instructions}"}</code> des messages.
      </div>
    </div>
  );
}

const lbl = {
  fontSize: 13,
  fontWeight: 700,
  color: DS.navy,
  marginBottom: 8,
};

const subLbl = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  marginBottom: 5,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

const inpStyle = {
  border: "1px solid #dde3ec",
  borderRadius: 7,
  padding: "8px 12px",
  fontSize: 13,
  outline: "none",
  background: "#f8fafd",
  fontFamily: DS.fontBody,
  width: "100%",
  boxSizing: "border-box",
};
